/**
 * Joins a list of cat names with locale-aware conjunction ("and" / "und").
 * Uses `Intl.ListFormat` so punctuation and conjunction vary by locale.
 *
 * @param names  Cat names to join.
 * @param locale BCP 47 tag, e.g. "en-GB" or "de-DE".
 */
export const formatCatNames = (names: string[], locale: string): string => {
  if (names.length === 0) {
    return "";
  }
  return new Intl.ListFormat(locale, { style: "long", type: "conjunction" }).format(names);
};

/**
 * Sibilant endings that take a bare apostrophe in German possessives.
 * e.g. Max → Max', Jess → Jess', Felix → Felix'
 */
const DE_SIBILANT_ENDINGS = /[sßxz]$/i;

/**
 * Formats a name in the possessive form for the given locale.
 *
 * English: "Milo" → "Milo's", "Jess" → "Jess'"
 * German:  "Milo" → "Milos",  "Max" → "Max'"
 */
export const formatPossessiveName = (name: string, locale: string): string => {
  if (locale.startsWith("de")) {
    return DE_SIBILANT_ENDINGS.test(name) ? `${name}'` : `${name}s`;
  }
  return name.endsWith("s") ? `${name}'` : `${name}'s`;
};

/**
 * Formats a list of cat names in possessive form, then joins them with
 * a locale-aware conjunction.
 *
 * @example
 * formatPossessiveCatNames(["Milo", "Luna"], "en-GB") // "Milo's and Luna's"
 * formatPossessiveCatNames(["Milo", "Luna"], "de-DE") // "Milos und Lunas"
 */
export const formatPossessiveCatNames = (names: string[], locale: string) =>
  formatCatNames(names.map((name) => formatPossessiveName(name, locale)), locale);
